import { type KeyboardEvent, type RefObject, useEffect, useRef, useState } from "react";

/**
 * A menu that opens from a button and closes on Escape or a click anywhere
 * else — the rail's workspace and brand menus.
 *
 * The click-away is a DOM listener, not data fetching, so it is an effect.
 */
export function useMenu(): {
  readonly open: boolean;
  readonly toggle: () => void;
  readonly close: () => void;
  readonly root: RefObject<HTMLDivElement | null>;
  readonly onKeyDown: (event: KeyboardEvent) => void;
} {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (event.target instanceof Node && root.current?.contains(event.target) !== true) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", away);
    return () => {
      document.removeEventListener("mousedown", away);
    };
  }, [open]);

  return {
    open,
    toggle: () => {
      setOpen((was) => !was);
    },
    close: () => {
      setOpen(false);
    },
    root,
    onKeyDown: (event) => {
      if (event.key === "Escape") setOpen(false);
    },
  };
}
